import { useState, type ReactNode } from 'react';
import {
  parsePercentInput,
  sanitizePercentInput,
} from '@/features/quotes/utils/group-life/format';

type Props = {
  name: string;
  /** Descrição oficial da cobertura (campo `Description` do InsureMO) —
   *  subtítulo principal cinza abaixo do nome. */
  description?: string;
  /** Linha auxiliar secundária abaixo da descrição (ex.: "Min/Max", "20% de
   *  X"). Renderizada em cinza mais claro quando há descrição. */
  caption?: ReactNode;
  /** Tag à direita do nome (ex: "Básica", "Adicional") — estilo azul outline. */
  badge?: string;
  enabled: boolean;
  onToggle: (next: boolean) => void;
  /** Controle do valor (input R$ / % / dropdown), renderizado à direita. */
  control?: ReactNode;
  disabled?: boolean;
  error?: string;
};

/**
 * Linha de cobertura do Step 2 (Plano), fiel ao "Switch Card" do Figma
 * (node 6816:113822):
 *  - Esquerda: nome (Bold 14, #0F161E) + badge azul outline +
 *    descrição (Regular 12, #777B7C).
 *  - Direita: Switch (Alba DS) + controle do valor, com gap de 24px.
 */
export function CoverageRowShell({
  name,
  description,
  caption,
  badge,
  enabled,
  onToggle,
  control,
  disabled,
  error,
}: Props) {
  return (
    <div className='py-1'>
      <div className='flex items-center justify-between gap-4'>
        {/* Esquerda: nome + badge + descrição */}
        <div className='flex flex-1 min-w-0 flex-col gap-1'>
          <div className='flex items-center gap-2 flex-wrap'>
            <span className="text-[14px] font-bold text-[#0F161E]">
              {name}
            </span>
            {badge && (
              <span className="rounded-[12px] bg-[rgba(44,40,100,0.2)] px-2 py-px text-[12px] font-bold text-[#2C2864]">
                {badge}
              </span>
            )}
          </div>
          {description && (
            <span className="text-[12px] font-normal text-[#777B7C]">
              {description}
            </span>
          )}
          {caption && (
            <span className="text-[11px] font-normal text-[#9FA4A6]">
              {caption}
            </span>
          )}
        </div>

        {/* Direita: toggle + controle do valor */}
        <div className='flex shrink-0 items-center justify-end gap-6'>
          <button
            type='button'
            role='switch'
            aria-checked={enabled}
            aria-label={`Ativar/desativar ${name}`}
            disabled={disabled}
            onClick={() => !disabled && onToggle(!enabled)}
            className={`relative inline-flex h-5 w-10 shrink-0 items-center rounded-full p-[3px] transition-colors
              focus:outline-none focus:ring-2 focus:ring-alba-violet-300 focus:ring-offset-2
              ${enabled ? 'bg-alba-green-500' : 'bg-[#CFD2D3]'}
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-90'}`}
          >
            <span
              className={`inline-block h-[14px] w-[14px] transform rounded-full bg-white shadow-sm transition-transform
                ${enabled ? 'translate-x-5' : 'translate-x-0'}`}
            />
          </button>
          {control}
        </div>
      </div>

      {error && (
        <div className='mt-1 text-right text-[12px] text-alba-error-red-500'>
          {error}
        </div>
      )}
    </div>
  );
}

/** Input de capital (R$) — borda #B7BBBC, rounded-12, padding generoso (Figma). */
export function CurrencyControl({
  value,
  onChange,
  disabled,
  invalid,
  placeholder = 'R$ 0,00',
}: {
  value: string;
  onChange: (raw: string) => void;
  disabled?: boolean;
  invalid?: boolean;
  placeholder?: string;
}) {
  return (
    <input
      type='text'
      inputMode='numeric'
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className={`w-[205px] rounded-[12px] border px-5 py-3 text-right text-[12px] outline-none transition-colors
        placeholder:text-[#9FA4A6] focus:ring-2 focus:ring-alba-violet-100
        ${invalid ? 'border-alba-error-red-500' : 'border-[#B7BBBC] focus:border-alba-violet-500'}
        ${disabled ? 'bg-alba-gray-50 text-[#9FA4A6] cursor-not-allowed' : 'bg-white text-[#0F161E]'}`}
    />
  );
}

/**
 * Input de percentual + valor resolvido (Figma: campo rounded-12). Aceita até
 * 2 casas decimais (vírgula como separador).
 */
export function PercentControl({
  percent,
  onChangePercent,
  resolvedLabel,
  disabled,
}: {
  percent: string;
  onChangePercent: (raw: string) => void;
  /**
   * Valor resolvido exibido abaixo do campo de %. String VAZIA reserva a
   * linha sem texto — é o que mantém a altura da linha igual ligada e
   * desligada, sem deslocar nome/switch (o card é `items-center`).
   * `undefined` = a linha nem existe (coberturas básicas).
   */
  resolvedLabel?: string;
  disabled?: boolean;
}) {
  // Rascunho do que foi digitado: preserva estados intermediários ("10,",
  // "10,50") que o valor numérico do pai não representa. Só é exibido
  // enquanto equivale ao `percent` recebido — se o pai ajustar o valor (ex.:
  // teto da cobertura), o valor do pai prevalece.
  const [draft, setDraft] = useState<string | null>(null);
  const displayed =
    draft != null && parsePercentInput(draft) === parsePercentInput(percent)
      ? draft
      : percent;

  return (
    <div className='flex w-[205px] flex-col items-end gap-1'>
      <div
        className={`flex w-full items-center rounded-[12px] border px-5 py-3 transition-colors
          ${disabled ? 'border-[#B7BBBC] bg-alba-gray-50' : 'border-[#B7BBBC] bg-white focus-within:border-alba-violet-500'}`}
      >
        <input
          type='text'
          inputMode='decimal'
          value={displayed}
          onChange={e => {
            const next = sanitizePercentInput(e.target.value);
            setDraft(next);
            onChangePercent(next);
          }}
          onBlur={() => setDraft(null)}
          disabled={disabled}
          className={`min-w-0 flex-1 bg-transparent text-right text-[12px] outline-none
            ${disabled ? 'text-[#9FA4A6] cursor-not-allowed' : 'text-[#0F161E]'}`}
        />
        <span className='ml-1 text-[12px] text-[#777B7C]'>%</span>
      </div>
      {resolvedLabel != null && (
        <span className='text-[12px] text-[#777B7C]'>
          {/* NBSP como placeholder: ocupa exatamente a mesma altura do valor
              formatado, então ligar/desligar a cobertura não mexe no layout. */}
          {resolvedLabel || '\u00A0'}
        </span>
      )}
    </div>
  );
}

/** Dropdown (Figma: campo rounded-12) — usado pelo BENEFIT_SELECTION. */
export function SelectControl({
  value,
  onChange,
  options,
  disabled,
  placeholder = 'Selecione',
}: {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
  placeholder?: string;
}) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      disabled={disabled}
      className={`w-[205px] rounded-[12px] border px-5 py-3 text-[12px] outline-none transition-colors
        border-[#B7BBBC] focus:border-alba-violet-500 focus:ring-2 focus:ring-alba-violet-100
        ${disabled ? 'bg-alba-gray-50 text-[#9FA4A6] cursor-not-allowed' : 'bg-white text-[#0F161E] cursor-pointer'}`}
    >
      <option value=''>{placeholder}</option>
      {options.map(o => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}
